import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserRole } from '../types/auth';
import { supabase } from '../lib/supabase';
import { Cache } from '../lib/cache';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, name: string, city?: string) => Promise<void>;
  updateUser: (data: { name?: string; email?: string; password?: string; city?: string; phone?: string }) => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set a timeout to ensure loading doesn't hang forever
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth loading timeout - forcing loading state to false');
      setIsLoading(false);
    }, 10000); // 10 second timeout

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      try {
        if (session?.user) {
          // Fetch user profile from database
          const { data: profile } = await supabase
            .from('profiles')
            .select('role, name, city, region, phone, approval_status, is_suspended')
            .eq('id', session.user.id)
            .single();

          if (profile?.approval_status === 'pending' || profile?.approval_status === 'rejected' || profile?.is_suspended) {
            await supabase.auth.signOut();
            setUser(null);
            setIsLoading(false);
            clearTimeout(loadingTimeout);
            return;
          }

          setUser({
            id: session.user.id,
            email: session.user.email!,
            role: (profile?.role as UserRole) || 'collaboratore',
            name: profile?.name || session.user.user_metadata?.name || 'User',
            city: profile?.city || session.user.user_metadata?.city,
            region: profile?.region,
            phone: profile?.phone,
            approval_status: profile?.approval_status || 'pending'
          });
        }
      } catch (error) {
        console.error('Error loading session:', error);
      } finally {
        setIsLoading(false);
        clearTimeout(loadingTimeout);
      }
    }).catch((error) => {
      console.error('Error getting session:', error);
      setIsLoading(false);
      clearTimeout(loadingTimeout);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        if (session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('role, name, city, region, phone, approval_status, is_suspended')
            .eq('id', session.user.id)
            .single();

          if (profile?.approval_status === 'pending' || profile?.approval_status === 'rejected' || profile?.is_suspended) {
            await supabase.auth.signOut();
            setUser(null);
            return;
          }

          setUser({
            id: session.user.id,
            email: session.user.email!,
            role: (profile?.role as UserRole) || 'collaboratore',
            name: profile?.name || session.user.user_metadata?.name || 'User',
            city: profile?.city || session.user.user_metadata?.city,
            region: profile?.region,
            phone: profile?.phone,
            approval_status: profile?.approval_status || 'pending'
          });
        } else {
          setUser(null);
        }
      })();
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) throw signInError;
      if (!signInData.user) throw new Error('No user returned after login');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, name, city, phone, approval_status, is_suspended')
        .eq('id', signInData.user.id)
        .single();

      if (profileError) throw profileError;

      if (profile?.approval_status === 'pending') {
        await supabase.auth.signOut();
        throw new Error('Il tuo account è in attesa di approvazione da parte di un amministratore.');
      }

      if (profile?.approval_status === 'rejected') {
        await supabase.auth.signOut();
        throw new Error('Il tuo account è stato rifiutato. Contatta l\'amministratore per maggiori informazioni.');
      }

      if (profile?.is_suspended) {
        await supabase.auth.signOut();
        throw new Error('Il tuo account è stato sospeso. Contatta l\'amministratore per maggiori informazioni.');
      }

      const role = (profile?.role as UserRole) || 'collaboratore';
      const name = profile?.name || signInData.user.user_metadata?.name || 'User';
      const city = profile?.city || signInData.user.user_metadata?.city;
      const phone = profile?.phone;

      setUser({
        id: signInData.user.id,
        email: signInData.user.email!,
        role,
        name,
        city,
        phone,
        approval_status: profile?.approval_status || 'approved'
      });

    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, name: string, city?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            role: 'collaboratore',
            ...(city && { city })
          }
        }
      });

      if (error) throw error;
      if (!data.user) throw new Error('No user returned after registration');

      // Sign out immediately after registration (user must wait for approval)
      await supabase.auth.signOut();

      // Throw a special message to inform the user
      throw new Error('PENDING_APPROVAL:Registrazione completata! Il tuo account è in attesa di approvazione da parte di un amministratore. Riceverai una notifica quando potrai accedere.');
    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const updateUser = async (data: { name?: string; email?: string; password?: string; city?: string; phone?: string }) => {
    try {
      const updateData: any = {};
      const metadata: any = {};

      if (data.name || data.city) {
        // Get current metadata
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const currentMetadata = currentUser?.user_metadata || {};

        metadata.role = currentMetadata.role || 'collaboratore';
        if (data.name) metadata.name = data.name;
        if (data.city) metadata.city = data.city;

        updateData.data = {
          ...currentMetadata,
          ...metadata
        };
      }

      if (data.email) {
        updateData.email = data.email;
      }
      if (data.password) {
        updateData.password = data.password;
      }

      const { data: updatedUser, error } = await supabase.auth.updateUser(updateData);

      if (error) throw error;
      if (!updatedUser.user) throw new Error('No user returned after update');

      // Update local user state
      setUser(prev => {
        if (!prev) return null;
        return {
          ...prev,
          email: data.email || prev.email,
          name: data.name || prev.name,
          city: data.city || prev.city,
          phone: data.phone !== undefined ? data.phone : prev.phone
        };
      });

      // Update profile in database if name, city or phone changed
      const profileUpdates: any = {};
      if (data.name !== undefined) profileUpdates.name = data.name;
      if (data.city !== undefined) profileUpdates.city = data.city;
      if (data.phone !== undefined) profileUpdates.phone = data.phone;

      if (Object.keys(profileUpdates).length > 0) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update(profileUpdates)
          .eq('id', updatedUser.user.id);

        if (profileError) throw profileError;
      }

    } catch (error: any) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // First clear local state and cache
      setUser(null);
      Cache.getInstance().clear();

      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, we want to clear the local state
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, register, updateUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};